import React, { useState, useEffect } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import {
  Calendar,
  ChevronDown,
  Plus,
  X,
  User,
  Mail,
  Phone,
  Tag,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  Loader,
  LayoutList,
  LayoutGrid,
} from "lucide-react";
import { Add } from "iconsax-reactjs";
import apiService from "../../services/api";

// Detect if touch device
const isTouchDevice = () => {
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
};

const ItemTypes = {
  TICKET: "ticket",
};

const statusConfig = {
  new: { label: "New", color: "bg-gray-700", icon: AlertCircle },
  in_progress: { label: "In Progress", color: "bg-yellow-600", icon: Loader },
  pending: { label: "Pending", color: "bg-orange-600", icon: Clock },
  resolved: { label: "Resolved", color: "bg-green-600", icon: CheckCircle },
  closed: { label: "Closed", color: "bg-red-600", icon: XCircle },
};

const priorityConfig = {
  low: { label: "Low", color: "bg-gray-700 text-gray-300" },
  medium: { label: "Medium", color: "bg-blue-600 text-blue-200" },
  high: { label: "High", color: "bg-orange-600 text-orange-200" },
  urgent: { label: "Urgent", color: "bg-red-600 text-red-200" },
};

const sourceConfig = {
  email: { label: "Email", icon: "📧" },
  phone: { label: "Phone", icon: "📞" },
  chat: { label: "Chat", icon: "💬" },
  portal: { label: "Portal", icon: "🌐" },
  api: { label: "API", icon: "🔌" },
};

// Ticket Card Component
const TicketCard = ({ ticket, onClick }) => {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.TICKET,
    item: { id: ticket.id, status: ticket.status },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div
      ref={drag}
      onClick={() => onClick(ticket)}
      className={`bg-[#0C0E12] border border-[#373A41] rounded-lg p-4 mb-3 cursor-pointer transition-all hover:bg-gray-800 hover:border-gray-600 ${
        isDragging ? "opacity-50" : "opacity-100"
      }`}
    >
      {/* Header with priority and assignees */}
      <div className="flex items-center justify-between mb-2">
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            priorityConfig[ticket.priority].color
          }`}
        >
          {priorityConfig[ticket.priority].label}
        </span>
        <div className="flex -space-x-2">
          {ticket.assignedUser && (
            <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-xs border-2 border-gray-800 text-white">
              {ticket.assignedUser.name.charAt(0)}
            </div>
          )}
        </div>
      </div>

      {/* Title */}
      <h3 className="text-white font-semibold mb-1">{ticket.title}</h3>

      {/* Description */}
      {ticket.description && (
        <p className="text-gray-400 text-sm mb-3 line-clamp-2">
          {ticket.description}
        </p>
      )}

      {/* Customer Info */}
      <div className="space-y-1 mb-3">
        <div className="flex items-center text-xs text-gray-400">
          <User className="w-3 h-3 mr-1" />
          <span>{ticket.customerName}</span>
        </div>
        <div className="flex items-center text-xs text-gray-400">
          <span className="mr-1">{sourceConfig[ticket.source]?.icon}</span>
          <span>{sourceConfig[ticket.source]?.label}</span>
        </div>
      </div>

      {/* Category Tag */}
      {ticket.category && (
        <span className="inline-block bg-gray-700 text-gray-300 text-xs px-2 py-1 rounded mb-3">
          {ticket.category}
        </span>
      )}

      {/* Due Date */}
      {ticket.dueDate && (
        <div className="inline-block px-2 py-1 rounded text-xs font-medium bg-gray-700 text-gray-300">
          Due {formatDate(ticket.dueDate)}
        </div>
      )}
    </div>
  );
};

// Column Component
const Column = ({ status, tickets, onDrop, onTicketClick }) => {
  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.TICKET,
    drop: (item) => onDrop(item.id, status),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <div
      ref={drop}
      className={`flex-shrink-0 w-full sm:w-80 transition-colors ${
        isOver ? "bg-gray-800/50 rounded-lg" : ""
      }`}
    >
      {/* Column Header */}
      <div className="mb-4">
        <span
          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-white text-sm font-medium ${statusConfig[status].color}`}
        >
          {statusConfig[status].label}
          <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
            {tickets.length}
          </span>
        </span>
      </div>

      {/* Tickets */}
      <div className="space-y-3">
        {tickets.map((ticket) => (
          <TicketCard key={ticket.id} ticket={ticket} onClick={onTicketClick} />
        ))}
        {tickets.length === 0 && (
          <div className="text-center py-8 text-gray-500 text-sm">
            No tickets
          </div>
        )}
      </div>
    </div>
  );
};

// Create Ticket Modal
const CreateTicketModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    status: "new",
    priority: "medium",
    source: "portal",
    category: "",
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    dueDate: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({
      title: "",
      description: "",
      status: "new",
      priority: "medium",
      source: "portal",
      category: "",
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      dueDate: "",
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-[#13161B] border border-gray-700 rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-white">Create New Ticket</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Title *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className="w-full px-4 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                rows="4"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="w-full px-4 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Customer Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.customerName}
                  onChange={(e) =>
                    setFormData({ ...formData, customerName: e.target.value })
                  }
                  className="w-full px-4 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Customer Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.customerEmail}
                  onChange={(e) =>
                    setFormData({ ...formData, customerEmail: e.target.value })
                  }
                  className="w-full px-4 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Customer Phone
                </label>
                <input
                  type="tel"
                  value={formData.customerPhone}
                  onChange={(e) =>
                    setFormData({ ...formData, customerPhone: e.target.value })
                  }
                  className="w-full px-4 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Category
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  className="w-full px-4 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData({ ...formData, priority: e.target.value })
                  }
                  className="w-full px-4 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {Object.entries(priorityConfig).map(([key, config]) => (
                    <option key={key} value={key}>
                      {config.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Source
                </label>
                <select
                  value={formData.source}
                  onChange={(e) =>
                    setFormData({ ...formData, source: e.target.value })
                  }
                  className="w-full px-4 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {Object.entries(sourceConfig).map(([key, config]) => (
                    <option key={key} value={key}>
                      {config.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  className="w-full px-4 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <option key={key} value={key}>
                      {config.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Due Date
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) =>
                    setFormData({ ...formData, dueDate: e.target.value })
                  }
                  className="w-full px-4 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded-lg text-white font-medium transition-all"
                style={{
                  background:
                    "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                }}
              >
                Create Ticket
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Ticket Details Modal
const TicketDetailsModal = ({
  ticket,
  isOpen,
  onClose,
  onDelete,
  onUpdate,
}) => {
  if (!isOpen || !ticket) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-[#13161B] border border-gray-700 rounded-lg max-w-3xl w-full max-h-screen overflow-y-auto">
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white">{ticket.title}</h2>
              <p className="text-sm text-gray-400 mt-1">Ticket #{ticket.id}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Status
              </label>
              <select
                value={ticket.status}
                onChange={(e) =>
                  onUpdate(ticket.id, { status: e.target.value })
                }
                className={`w-full px-4 py-2 rounded-lg text-sm font-medium border-0 ${
                  statusConfig[ticket.status].color
                } text-white`}
              >
                {Object.entries(statusConfig).map(([key, config]) => (
                  <option key={key} value={key}>
                    {config.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Priority
              </label>
              <span
                className={`inline-block px-4 py-2 rounded-lg text-sm font-medium ${
                  priorityConfig[ticket.priority].color
                }`}
              >
                {priorityConfig[ticket.priority].label}
              </span>
            </div>
          </div>

          {ticket.description && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <p className="text-white bg-[#0C0E12] p-4 rounded-lg border border-gray-700">
                {ticket.description}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Customer
              </label>
              <div className="space-y-1 bg-[#0C0E12] p-4 rounded-lg border border-gray-700">
                <p className="text-white font-medium">{ticket.customerName}</p>
                <p className="text-sm text-gray-400">{ticket.customerEmail}</p>
                {ticket.customerPhone && (
                  <p className="text-sm text-gray-400">
                    {ticket.customerPhone}
                  </p>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Details
              </label>
              <div className="space-y-2 bg-[#0C0E12] p-4 rounded-lg border border-gray-700">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <span>Source:</span>
                  <span className="text-white">
                    {sourceConfig[ticket.source]?.label}
                  </span>
                </div>
                {ticket.category && (
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <span>Category:</span>
                    <span className="text-white">{ticket.category}</span>
                  </div>
                )}
                {ticket.dueDate && (
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <span>Due Date:</span>
                    <span className="text-white">
                      {formatDate(ticket.dueDate)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {ticket.assignedUser && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Assigned To
              </label>
              <div className="flex items-center gap-3 bg-[#0C0E12] p-3 rounded-lg border border-gray-700">
                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                  <span className="text-sm font-medium text-white">
                    {ticket.assignedUser.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-white">
                    {ticket.assignedUser.name}
                  </p>
                  <p className="text-sm text-gray-400">
                    {ticket.assignedUser.email}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
            <button
              onClick={() => {
                onDelete(ticket.id);
                onClose();
              }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Delete Ticket
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Tickets Component
const Tickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [viewMode, setViewMode] = useState("kanban");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterSource, setFilterSource] = useState("all");

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await apiService.getTickets(); // Use getTickets() method
      setTickets(response.data || []); // Adjust based on actual response structure
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (formData) => {
    try {
      await apiService.createTicket(formData); // Use createTicket() method
      setShowCreateModal(false);
      fetchTickets();
    } catch (error) {
      console.error("Error creating ticket:", error);
      alert(error.response?.data?.message || "Failed to create ticket");
    }
  };

  const handleUpdateTicket = async (id, updates) => {
    try {
      await apiService.updateTicket(id, updates); // Use updateTicket() method
      fetchTickets();
      if (selectedTicket && selectedTicket.id === id) {
        setSelectedTicket({ ...selectedTicket, ...updates });
      }
    } catch (error) {
      console.error("Error updating ticket:", error);
      alert(error.response?.data?.message || "Failed to update ticket");
    }
  };

  const handleDeleteTicket = async (id) => {
    if (window.confirm("Are you sure you want to delete this ticket?")) {
      try {
        await apiService.deleteTicket(id); // Use deleteTicket() method
        fetchTickets();
      } catch (error) {
        console.error("Error deleting ticket:", error);
        alert(error.response?.data?.message || "Failed to delete ticket");
      }
    }
  };

  const handleDrop = async (ticketId, newStatus) => {
    await handleUpdateTicket(ticketId, { status: newStatus });
  };

  const filteredTickets = tickets.filter((ticket) => {
    const matchesStatus =
      filterStatus === "all" || ticket.status === filterStatus;
    const matchesPriority =
      filterPriority === "all" || ticket.priority === filterPriority;
    const matchesSource =
      filterSource === "all" || ticket.source === filterSource;
    return matchesStatus && matchesPriority && matchesSource;
  });

  const getTicketsByStatus = (status) => {
    return filteredTickets.filter((ticket) => ticket.status === status);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0C0E12] flex items-center justify-center">
        <Loader className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <DndProvider backend={isTouchDevice() ? TouchBackend : HTML5Backend}>
      <div className="min-h-screen bg-[#0C0E12] text-white font-inter">
        <div className="p-4 lg:p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 text-white">
                Tickets
              </h1>
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
                Manage and track customer support requests
              </p>
            </div>

            <div className="flex flex-col md:flex-row gap-2 sm:gap-3 shrink-0">
              <button className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm sm:text-base font-medium min-w-0">
                <span className="whitespace-nowrap">Bulk Actions</span>
              </button>

              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
                style={{
                  background:
                    "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                  border: "1px solid",
                  borderImageSource:
                    "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                  boxShadow: `
                    0px 36px 32px -16px #427BCA36,
                    0px 8px 16px 0px #2F578F1F,
                    0px 2px 4px -8px #427BCA14,
                    0px 8px 24px -16px #FFFFFF3D inset,
                    0px -24px 32px 0px #FFFFFF38 inset,
                    0px 0px 12px 0px #FFFFFF inset
                  `,
                  borderImageSlice: "1",
                }}
              >
                <Add size={16} className="shrink-0" />
                <span className="whitespace-nowrap">New Ticket</span>
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            <div className="flex flex-wrap gap-2">
              <select
                value={filterSource}
                onChange={(e) => setFilterSource(e.target.value)}
                className="px-3 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-sm hover:bg-gray-700 transition-colors text-gray-300"
              >
                <option value="all">All Sources</option>
                {Object.entries(sourceConfig).map(([key, config]) => (
                  <option key={key} value={key}>
                    {config.label}
                  </option>
                ))}
              </select>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-sm hover:bg-gray-700 transition-colors text-gray-300"
              >
                <option value="all">All Status</option>
                {Object.entries(statusConfig).map(([key, config]) => (
                  <option key={key} value={key}>
                    {config.label}
                  </option>
                ))}
              </select>

              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="px-3 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-sm hover:bg-gray-700 transition-colors text-gray-300"
              >
                <option value="all">All Priority</option>
                {Object.entries(priorityConfig).map(([key, config]) => (
                  <option key={key} value={key}>
                    {config.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setViewMode("kanban")}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === "kanban"
                    ? "bg-blue-600 text-white"
                    : "bg-[#0C0E12] border border-gray-700 text-gray-400 hover:bg-gray-700"
                }`}
              >
                <LayoutGrid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === "list"
                    ? "bg-blue-600 text-white"
                    : "bg-[#0C0E12] border border-gray-700 text-gray-400 hover:bg-gray-700"
                }`}
              >
                <LayoutList className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Kanban Board */}
          {viewMode === "kanban" && (
            <div className="px-4 sm:px-6 lg:px-8 py-6 bg-[#13161B] rounded-lg">
              <div className="flex flex-col sm:flex-row gap-6 overflow-x-auto pb-4">
                {Object.keys(statusConfig).map((status) => (
                  <Column
                    key={status}
                    status={status}
                    tickets={getTicketsByStatus(status)}
                    onDrop={handleDrop}
                    onTicketClick={(ticket) => {
                      setSelectedTicket(ticket);
                      setShowDetailsModal(true);
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* List View */}
          {viewMode === "list" && (
            <div className="bg-[#13161B] rounded-lg border border-gray-700 overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#0C0E12] border-b border-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Ticket
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {filteredTickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      className="hover:bg-gray-800 cursor-pointer"
                      onClick={() => {
                        setSelectedTicket(ticket);
                        setShowDetailsModal(true);
                      }}
                    >
                      <td className="px-6 py-4">
                        <div className="font-medium text-white">
                          {ticket.title}
                        </div>
                        <div className="text-sm text-gray-400">
                          #{ticket.id}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-white">
                          {ticket.customerName}
                        </div>
                        <div className="text-sm text-gray-400">
                          {ticket.customerEmail}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            statusConfig[ticket.status].color
                          } text-white`}
                        >
                          {statusConfig[ticket.status].label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            priorityConfig[ticket.priority].color
                          }`}
                        >
                          {priorityConfig[ticket.priority].label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-white">
                        {sourceConfig[ticket.source]?.label}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTicket(ticket.id);
                          }}
                          className="text-red-500 hover:text-red-400"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredTickets.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No tickets found
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modals */}
        <CreateTicketModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateTicket}
        />

        <TicketDetailsModal
          ticket={selectedTicket}
          isOpen={showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
          onDelete={handleDeleteTicket}
          onUpdate={handleUpdateTicket}
        />
      </div>
    </DndProvider>
  );
};

export default Tickets;
